/* eslint-disable react/no-array-index-key */
// dependencies
import { List, Map, fromJS } from "immutable";
import PropTypes from "prop-types";
import React, { useState } from "react";

import { faCheck, faTimes } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { InputWrapper } from "@performyard/py-components";

import BeforeUnload from "../BeforeUnload";
// components
import LongText from "../Inputs/LongText";
import { SingleSelectForm } from "../Inputs/SelectForm";
import TextInput from "../Inputs/TextInput";
import Toggle from "../Inputs/ToggleSwitches/ThumbSwitch";
// styles
import css from "./ReviewFormCard.module.scss";

/**
 * Creates a card for a review form.
 * @param {object} props
 */
const ExternalReviewFormCard = (props) => {
	const {
		className = "",
		name,
		authorName,
		authorEmail,
		emailMessage,
		visibleToSubject,
		isEditing = false,
		editableForm = undefined,
		redacted,
		lockedFields = List(),
		isNewForm,
		isDeleted = false,
		onEdit = () => {},
		onDelete = () => {},
		onCancel = () => {},
		onSave = () => {},
	} = props;

	const formIsSelectable = editableForm !== undefined;

	const [localRedacted, setRedacted] = useState(redacted);
	const [localAuthorName, setAuthorName] = useState(authorName);
	const [localAuthorEmail, setAuthorEmail] = useState(authorEmail);
	const [localEmailMessage, setEmailMessage] = useState(emailMessage);
	const [localVisibleToSubject, setVisibleToSubject] = useState(visibleToSubject);
	const [localEditableForm, setLocalEditableForm] = useState(editableForm === null ? "" : editableForm);

	const formSelected = !(formIsSelectable && localEditableForm === "");

	/**
	 * True if we have at least 1 error.
	 */
	const hasError = [formSelected].indexOf(false) >= 0;

	/**
	 * The redactedness of an a form can only be edited by an admin.
	 * A group admin cannot toggle the redactedness.
	 * For a group admin and a redacted form -
	 *      always show the green checkmark (even if they are editing)
	 * For a group admin and a not redacted form -
	 *      do not show the field on the card at all.
	 */
	let redactedSection = null;

	if (lockedFields.includes("redactedness")) {
		redactedSection = redacted ? (
			<div data-testid="redactValue" className={`${css.attributeValue} ${redacted ? css.isGreen : css.isRed}`}>
				<FontAwesomeIcon icon={faCheck} data-testid="fas-check" />
			</div>
		) : null;
	} else {
		redactedSection = isEditing ? (
			<div className={css.toggleWrapper}>
				<Toggle onChange={setRedacted} value={localRedacted} width={40} />
			</div>
		) : (
			<div data-testid="redactValue" className={`${css.attributeValue} ${redacted ? css.isGreen : css.isRed}`}>
				{redacted ? (
					<FontAwesomeIcon icon={faCheck} data-testid="fas-check" />
				) : (
					<FontAwesomeIcon icon={faTimes} data-testid="fas-times" />
				)}
			</div>
		);
	}

	// check if there are any differences between local and saved values
	const checkFormChange = () => {
		const localForm = localEditableForm ? localEditableForm.get("id", null) : null;
		const savedForm = editableForm ? editableForm.get("id", null) : null;

		const allFormAttributes = {
			formChanged: localForm !== savedForm,
			authorNameChanged: localAuthorName !== authorName,
			authorEmailChanged: localAuthorEmail !== authorEmail,
			emailMessageChanged: localEmailMessage !== emailMessage,
			visibleToSubjectChanged: localVisibleToSubject !== visibleToSubject,
			redactedChanged: localRedacted !== redacted,
		};

		return Object.values(allFormAttributes).some((value) => value === true);
	};

	const handleSave = () => {
		onSave({
			authorName: localAuthorName,
			authorEmail: localAuthorEmail,
			emailMessage: localEmailMessage,
			visibleToSubject: localVisibleToSubject,
			redacted: localRedacted,
			form: formIsSelectable && localEditableForm !== null ? localEditableForm.toJS() : null,
		});
	};

	const handleCancel = () => {
		// reset defaults
		setAuthorName(authorName);
		setAuthorEmail(authorEmail);
		setEmailMessage(emailMessage);
		setVisibleToSubject(visibleToSubject);
		setRedacted(redacted);
		setLocalEditableForm(editableForm);

		// cancel hook
		onCancel();
	};

	const handleReviewFormChange = (value) => {
		// Convert from new format {_id, name} to legacy immutable format {id, name}
		if (value) {
			const legacyValue = fromJS({
				id: value._id,
				name: value.name,
			});
			setLocalEditableForm(legacyValue);
		} else {
			setLocalEditableForm(value);
		}
	};

	return (
		<div
			data-testid="externalReviewFormCard"
			className={`${css.templateForm} ${isEditing ? css.templateIsEditing : ""} ${className}`}
		>
			<div className={css.content}>
				<div>
					{formIsSelectable && isDeleted && <div className={css.isDeletedLabel}>Form has been deleted</div>}
					{formIsSelectable && (
						<div data-testid="select-review-form" className={css.attributeContainer}>
							<div className={`${css.attributeLabel} ${css.gridCell}`}>Select a form:</div>
							{isEditing ? (
								<InputWrapper id="addForm">
									<SingleSelectForm
										value={
											localEditableForm && typeof localEditableForm.get === "function"
												? {
														_id: localEditableForm.get("id"),
														name: localEditableForm.get("name"),
													}
												: undefined
										}
										onChange={handleReviewFormChange}
										placeholder="Enter form name..."
									/>
								</InputWrapper>
							) : (
								name
							)}
						</div>
					)}
					{!formIsSelectable && (
						<div data-testid="formName" className={css.formName}>
							{name}
						</div>
					)}

					<div data-testid="reviewersNameContainer" className={css.attributeContainer}>
						<div className={css.attributeLabel}>{"Reviewer's name:"}</div>
						<div className={css.attributeValue} data-testid="reviewerName">
							{isEditing ? (
								<div className={css.textInputContainer}>
									<TextInput onChange={setAuthorName} value={localAuthorName} />
								</div>
							) : (
								authorName
							)}
						</div>
					</div>

					<div data-testid="reviewerEmailContainer" className={css.attributeContainer}>
						<div className={css.attributeLabel}>{"Reviewer's email:"}</div>
						<div className={css.attributeValue} data-testid="reviewerEmail">
							{isEditing ? (
								<div className={css.textInputContainer}>
									<TextInput onChange={setAuthorEmail} value={localAuthorEmail} />
								</div>
							) : (
								authorEmail
							)}
						</div>
					</div>

					<div className={css.attributeContainer}>
						<div className={css.attributeLabel}>Email message:</div>
						<div className={css.attributeValue} data-testid="emailMessage">
							{isEditing ? (
								<div className={css.textInputContainer}>
									<LongText onChange={setEmailMessage} value={localEmailMessage} />
								</div>
							) : (
								emailMessage
							)}
						</div>
					</div>

					<div data-testid="visibilityContainer" className={`${css.attributeContainer} ${css.nonGrid}`}>
						<div className={css.attributeLabel}>
							Visible to the subject (when all authors/signers are finished):
						</div>
						{isEditing ? (
							<div className={css.toggleWrapper}>
								<Toggle onChange={setVisibleToSubject} value={localVisibleToSubject} width={40} />
							</div>
						) : (
							<div className={`${css.attributeValue} ${visibleToSubject ? css.isGreen : css.isRed}`}>
								{visibleToSubject ? (
									<FontAwesomeIcon data-testid="fas-check" icon={faCheck} />
								) : (
									<FontAwesomeIcon data-testid="fas-times" icon={faTimes} />
								)}
							</div>
						)}
					</div>
					{redactedSection ? (
						<div data-testid="redactedContainer" className={`${css.attributeContainer} ${css.nonGrid}`}>
							<div className={css.attributeLabel}>Redact authors:</div>
							{redactedSection}
						</div>
					) : null}
				</div>
			</div>

			<div data-testid="cardActions" className={css.cardActions}>
				{isEditing ? (
					<div>
						<div
							data-testid="saveButton"
							className={`${css.action} ${css.blue} ${hasError ? css.isDisabled : ""}`}
							onClick={hasError ? () => {} : handleSave}
						>
							{isNewForm ? "Add" : "Update"}
						</div>
						<div data-testid="cancelButton" className={`${css.action} ${css.white}`} onClick={handleCancel}>
							Cancel
						</div>
					</div>
				) : (
					<div>
						<div data-testid="editButton" className={`${css.action} ${css.blue}`} onClick={onEdit}>
							Edit
						</div>
						<div data-testid="deleteButton" className={`${css.action} ${css.red}`} onClick={onDelete}>
							Delete
						</div>
					</div>
				)}

				<div className={css.cardErrors}>
					{formSelected ? null : <div className={css.cardError}>A form must be selected.</div>}
				</div>
			</div>
			<BeforeUnload checkForUnsavedData={checkFormChange} />
		</div>
	);
};

ExternalReviewFormCard.propTypes = {
	/** Handle clicking the edit action */
	onEdit: PropTypes.func,
	/** Handle clicking the delete action */
	onDelete: PropTypes.func,
	/** Handle clicking the cancel action */
	onCancel: PropTypes.func,
	/** Handle clicking the save action */
	onSave: PropTypes.func,
	/** Will put the card in edit mode if true */
	isEditing: PropTypes.bool,
	/** Additional class */
	className: PropTypes.string,
	/** Will display a green check or red 'x' */
	visibleToSubject: PropTypes.bool.isRequired,
	redacted: PropTypes.bool.isRequired,
	isDeleted: PropTypes.bool,
	/** Name of the form */
	name: PropTypes.string.isRequired,
	/** name of author */
	authorName: PropTypes.string.isRequired,
	/** email address of author */
	authorEmail: PropTypes.string.isRequired,
	/** email message */
	emailMessage: PropTypes.string.isRequired,
	/** the selected form for the dropdown. If this value is `undefined`, then
	 * the dropdown is not displayed. This can also be null if nothing is
	 * selected. */
	editableForm: PropTypes.instanceOf(Map),
	lockedFields: PropTypes.instanceOf(List),
	isNewForm: PropTypes.bool.isRequired,
};

export default ExternalReviewFormCard;
